/* eslint-disable @typescript-eslint/no-shadow */

import { useCallback, useEffect } from "react";
import { useDashboardContext } from "../../contexts/dashboard-context";
import { usePreviewContext } from "../../contexts/preview-context";
import { usePhotoPreprocessProgress } from "../../hooks/use-photo-preprocess-progress";
import { useBottomMenu } from "../../hooks/useBottomMenu";
import { useEditor } from "../../hooks/useEditor";
import { useIsPreviewMode } from "../../hooks/useIsPreviewMode";
import { useIsTransformMode } from "../../hooks/useIsTransformMode";
import { useMessage } from "../../hooks/useMessage";
import { IPreviewOverlayRef } from "../preview-overlay";
import { ISlidesShowPreviewRef } from "./slideshow-preview";
import { ISpreadSlideShowItem } from "./spread-slideshow-item";

/** This function is intended to contain the logic for the overlay zones on the preview */
export function usePreviewOverlay(
	slideShowPreviewRef: React.RefObject<ISlidesShowPreviewRef>,
	previewOverlayRef: React.RefObject<IPreviewOverlayRef>,
	selectedZone: eXpress.core.IZone | undefined,
	selectedSpread: eXpress.core.Spread | undefined,
	mutable: {
		slideShowItems: Map<eXpress.core.Spread, ISpreadSlideShowItem>;
	},
) {
	const dashboard = useDashboardContext();
	const editor = useEditor();
	const { logger } = editor;
	const { stack } = useBottomMenu();
	const [photoPreprocessProgress] = usePhotoPreprocessProgress();
	const { getScreenToSpreadTransform } = usePreviewContext();
	const [isTransformMode] = useIsTransformMode();

	// Update zone state
	const updateZoneState = useCallback(
		(zone: eXpress.core.IZone | undefined, previewOverlay: IPreviewOverlayRef) => {
			if (!zone) return;

			// Get Icon to show on overlay
			function getOverlayIcon(zone: eXpress.core.IZone | undefined) {
				if (eXpress.core.isPhotoZone(zone)) {
					if (zone.isEmpty) {
						const isSelected = selectedZone === zone;
						const isPhotoSelectionOpen = stack.find((s) => s.id === "photo-selection");
						const isPhotoSelectionEmpty = editor.assets.photoSelection.length === 0;
						const addIcon = photoPreprocessProgress ? "add-disabled" : "add";
						return isSelected && (isPhotoSelectionOpen || isPhotoSelectionEmpty)
							? addIcon
							: undefined;
					}
					if (zone.getPhotoLook()?.photo?.state === eXpress.core.PhotoState.Rejected)
						return "cross";
					if (!zone.getPhotoLook()?.isQualityOk()) return "low-resolution";
					if (isTransformMode && zone === selectedZone && dashboard.canPanPhoto(zone))
						return "pan";
				}
				return undefined;
			}
			previewOverlay.updateZoneState(zone, {
				icon: getOverlayIcon(zone),
			});
		},
		[selectedZone, isTransformMode, dashboard, stack, editor, photoPreprocessProgress],
	);

	const [isPreviewMode] = useIsPreviewMode();

	/** Add overlay information for a spread */
	const addSpreadOverlay = useCallback(
		(spread: eXpress.core.Spread | undefined, previewOverlay: IPreviewOverlayRef) => {
			if (!spread) return;

			// Don't add overlays in PreviewMode
			if (isPreviewMode) return;

			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			// I think we later need:
			// - a spreadsInView array (We now use the selected Spread)
			// - Show when spread is rendered, not upfront (set opacity to prevent browser rerender during animation?)
			const transform = getScreenToSpreadTransform(spread, logger);
			if (transform) {
				previewOverlay.addSpread(spread, transform);

				// Start with hidden spread overlays divs, will be shown when rendered
				const item = mutable.slideShowItems.get(spread);
				if (item) {
					previewOverlay.hideSpread(spread, item.isLoading);

					// Set correct state of each zone
					spread.zones.forEach((zone) => updateZoneState(zone, previewOverlay));
				}
			}
		},
		[
			getScreenToSpreadTransform,
			isPreviewMode,
			logger,
			mutable.slideShowItems,
			slideShowPreviewRef,
			updateZoneState,
		],
	);

	// Add/remove overlays when selected Spread changes
	useEffect(
		function placeOverlayOnSelectedSpread() {
			if (!selectedSpread) return undefined;

			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return undefined;

			const previewOverlay = previewOverlayRef.current;
			if (!previewOverlay) return undefined;

			addSpreadOverlay(selectedSpread, previewOverlay);

			return () => {
				previewOverlay.removeSpread(selectedSpread);
			};
		},
		[selectedSpread, previewOverlayRef, slideShowPreviewRef, addSpreadOverlay],
	);

	// Resize overlay divs on zoom
	useEffect(
		function updatePreviewOverlayOnTransformChange() {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return undefined;

			const previewOverlay = previewOverlayRef.current;
			if (!previewOverlay) return undefined;

			// Update on slideshow transform change (like zoom)
			const unsubscribeTransform = slideShow.onTransformChange.subscribe(
				function onTransformChange() {
					if (!selectedSpread) return;
					const transform = getScreenToSpreadTransform(selectedSpread, logger);
					if (transform) previewOverlay.updateSpreadLayout(selectedSpread, transform);
				},
			);

			// Update if slideshow item is resized/repositioned
			const unsubscribeItemTransform = slideShow.onItemTransformChange.subscribe(
				function onItemTransformChange(slideShowItemInfo) {
					// Only apply transform for current item
					if (slideShowItemInfo.item.spread === selectedSpread) {
						const transform = getScreenToSpreadTransform(selectedSpread, logger);
						if (transform) previewOverlay.updateSpreadLayout(selectedSpread, transform);
					}
				},
			);

			return () => {
				unsubscribeItemTransform();
				unsubscribeTransform();
			};
		},
		[
			getScreenToSpreadTransform,
			logger,
			previewOverlayRef,
			selectedSpread,
			slideShowPreviewRef,
		],
	);

	// Update zone overlay state of existing zone
	useMessage(
		useCallback(
			(message) => {
				const previewOverlay = previewOverlayRef.current;
				if (!previewOverlay) return;

				// Photo changed?
				if (eXpress.core.isPhotoInExistingZoneChanged(message)) {
					message.zones.forEach((zone) => {
						updateZoneState(zone, previewOverlay);
					});
				}

				if (message instanceof eXpress.core.PhotoLookCropChangedMessage) {
					updateZoneState(selectedZone, previewOverlay);
				}

				// Is a partner photo downloaded and rejected on the server for this spread;
				// Remark: Is this correct?
				// If a command changes the spread selection, selectedSpread will still be the old one.
				// Probably in the next react commit phase, this will become OK. The changed selection will trigger the useEffect (with selectedSpread in deps)
				if (eXpress.core.isPhotoStateChangedForSpreadPhotos(message, selectedSpread)) {
					selectedSpread?.photoZones.forEach((zone) => {
						updateZoneState(zone, previewOverlay);
					});
				}

				// Update when zones are changed (like: apply template)
				if (eXpress.core.canZoneBeAddedOrRemoved(message)) {
					if (eXpress.core.affectsSpreads(message)) {
						const spreads = message.getAffectedSpreads();
						spreads.forEach((spread) => {
							if (previewOverlayRef.current?.hasSpread(spread)) {
								addSpreadOverlay(spread, previewOverlay);
							}
						});
					} else if (!eXpress.core.isNewDefinitionLoaded(message)) {
						logger.error(
							`Expected message: '${eXpress.core.getMessageName(
								message,
							)}' to affect a spread.`,
						);
					}
				}

				if (eXpress.core.isSpreadCollectionChanged(message)) {
					if (message instanceof eXpress.core.DefinitionSpreadCollectionChangedMessage) {
						editor.definition
							?.getFillablePhotoZones(false)
							.forEach((photoZone) => updateZoneState(photoZone, previewOverlay));
					}
				}

				// TODO: update when zoomed? -> bad-quality
			},
			[
				previewOverlayRef,
				selectedSpread,
				updateZoneState,
				selectedZone,
				addSpreadOverlay,
				logger,
				editor.definition,
			],
		),
	);

	// Update overlay(interactive) when zone is transformed (move, rotate, resize)
	useMessage(
		useCallback(
			function updatePreviewOverlayOnInteractiveMessage(message) {
				const previewOverlay = previewOverlayRef.current;
				if (!previewOverlay) return;
				if (
					message instanceof eXpress.core.SpreadRenderedMessage &&
					message.spread.zones.includes(selectedZone as eXpress.core.TransformableZone)
				) {
					const spread = selectedZone?.spread;
					if (spread) {
						const transform = getScreenToSpreadTransform(selectedSpread, logger);
						if (transform) previewOverlay.updateZoneLayout(selectedZone, transform);
					}
				}
			},
			[getScreenToSpreadTransform, logger, previewOverlayRef, selectedSpread, selectedZone],
		),
		{ captureInteractiveMessages: true },
	);

	// Update state if zone selection changes (pan icon only on selected zones)
	useEffect(() => {
		const previewOverlay = previewOverlayRef.current;
		if (!previewOverlay) return;

		updateZoneState(selectedZone, previewOverlay);

		// Should we update the state if the zone is deselected? (in the effect cleanup function)
		// Currently, it doesn't seems to be needed.
	}, [previewOverlayRef, selectedZone, updateZoneState]);
}
